import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { questionApi } from '../api/questionApi';
import { questionQueryKeys } from './queryKeys';
import { CreateQuestionDTO, UpdateQuestionDTO, QuestionDTO, ReorderQuestionsDTO } from '../dtos';
import { useNotifications } from '@shared/components/notifications';
import { usePostHog } from '../../../hooks/usePostHog';
import { SURVEY_EVENTS } from '../../../utils/trackingEvents';

// Get questions by survey
export const useQuestionsBySurvey = (surveyId: string) => {
  return useQuery({
    queryKey: questionQueryKeys.questionsBySurvey(surveyId),
    queryFn: () => questionApi.getQuestionsBySurvey(surveyId),
    enabled: !!surveyId,
  });
};

// Create question mutation
export const useCreateQuestion = (surveyId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { captureEvent } = usePostHog();

  return useMutation({
    mutationFn: (data: CreateQuestionDTO) => questionApi.createQuestion(surveyId, data),
    onSuccess: (response, variables) => {
      // Track question creation
      captureEvent(SURVEY_EVENTS.QUESTION_ADDED, {
        survey_id: surveyId,
        question_id: response.data?.id,
        question_type: variables.type,
        question_text: variables.title['en'],
        is_required: variables.isRequired || false,
        has_options: variables.options && variables.options.length > 0,
        options_count: variables.options ? variables.options.length : 0,
      });

      // Invalidate and refetch questions for this survey
      queryClient.invalidateQueries({
        queryKey: questionQueryKeys.questionsBySurvey(surveyId),
      });

      // Invalidate stats
      queryClient.invalidateQueries({
        queryKey: questionQueryKeys.questionStats(surveyId),
      });

      notifications.success('Question created successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to create question';
      notifications.error(message);
    },
  });
};

// Update question mutation
export const useUpdateQuestion = (surveyId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: ({ questionId, data }: { questionId: string; data: UpdateQuestionDTO }) =>
      questionApi.updateQuestion(surveyId, questionId, data),
    onSuccess: (response, variables) => {
      // Invalidate specific question
      queryClient.invalidateQueries({
        queryKey: questionQueryKeys.question(surveyId, variables.questionId),
      });

      // Invalidate questions list for this survey
      queryClient.invalidateQueries({
        queryKey: questionQueryKeys.questionsBySurvey(surveyId),
      });

      // Invalidate stats
      queryClient.invalidateQueries({
        queryKey: questionQueryKeys.questionStats(surveyId),
      });

      notifications.success('Question updated successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to update question';
      notifications.error(message);
    },
  });
};

// Delete question mutation
export const useDeleteQuestion = (surveyId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { captureEvent } = usePostHog();

  return useMutation({
    mutationFn: (questionId: string) => questionApi.deleteQuestion(surveyId, questionId),
    onSuccess: (response, questionId) => {
      // Track question deletion (response may not contain question data)
      captureEvent(SURVEY_EVENTS.QUESTION_DELETED, {
        survey_id: surveyId,
        question_id: questionId,
        deleted_question_type: 'unknown', // API may not return deleted question details
        was_required: false,
        had_options: false,
        options_count: 0,
      });

      // Remove from cache
      queryClient.removeQueries({
        queryKey: questionQueryKeys.question(surveyId, questionId),
      });

      // Invalidate questions list for this survey
      queryClient.invalidateQueries({
        queryKey: questionQueryKeys.questionsBySurvey(surveyId),
      });

      // Invalidate stats
      queryClient.invalidateQueries({
        queryKey: questionQueryKeys.questionStats(surveyId),
      });

      notifications.success('Question deleted successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to delete question';
      notifications.error(message);
    },
  });
};

// Reorder questions mutation
export const useReorderQuestions = (surveyId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { captureEvent } = usePostHog();

  return useMutation({
    mutationFn: (data: ReorderQuestionsDTO) => questionApi.reorderQuestions(surveyId, data),
    onSuccess: (response, variables) => {
      // Track question reordering
      captureEvent(SURVEY_EVENTS.QUESTION_REORDERED, {
        survey_id: surveyId,
        questions_count: variables.questionOrders?.length || 0,
        reorder_method: 'drag_drop',
        moved_question_id: variables.questionOrders?.[0]?.questionId || null,
        question_ids: variables.questionOrders?.map((order) => order.questionId) || [],
        new_orders: variables.questionOrders?.map((order) => order.newOrder) || [],
      });

      // Silently sync cache with server response instead of invalidating.
      // Invalidation triggers a refetch that briefly clears the UI and causes a jump.
      // The optimistic update already set the correct visual state; this ensures
      // the cache also reflects server-side changes (e.g. updatedAt, updatedBy).
      if (response?.data?.updatedQuestions) {
        const updatedMap = new Map(
          response.data.updatedQuestions.map((q: QuestionDTO) => [q.id, q])
        );
        queryClient.setQueryData(
          questionQueryKeys.questionsBySurvey(surveyId),
          (oldData: unknown) => {
            const typed = oldData as { data?: { questions?: QuestionDTO[] } } | undefined;
            if (!typed?.data?.questions) return oldData;

            return {
              ...typed,
              data: {
                ...typed.data,
                questions: typed.data.questions.map((q) => updatedMap.get(q.id) ?? q),
              },
            };
          }
        );
      }

      notifications.success('Questions reordered successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to reorder questions';
      notifications.error(message);
    },
  });
};

// ===== TEMPLATE QUESTION HOOKS =====

// Get questions by template
export const useQuestionsByTemplate = (tenantId: string, templateId: string) => {
  return useQuery({
    queryKey: questionQueryKeys.questionsByTemplate(tenantId, templateId),
    queryFn: () => questionApi.getQuestionsByTemplate(tenantId, templateId),
    enabled: !!tenantId && !!templateId,
  });
};

// Create template question mutation
export const useCreateTemplateQuestion = (tenantId: string, templateId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: (data: CreateQuestionDTO) =>
      questionApi.createTemplateQuestion(tenantId, templateId, data),
    onSuccess: (_response) => {
      // Invalidate and refetch questions for this template
      queryClient.invalidateQueries({
        queryKey: questionQueryKeys.questionsByTemplate(tenantId, templateId),
      });

      notifications.success('Template question created successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to create template question';
      notifications.error(message);
    },
  });
};

// Update template question mutation
export const useUpdateTemplateQuestion = (tenantId: string, templateId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: ({ questionId, data }: { questionId: string; data: UpdateQuestionDTO }) =>
      questionApi.updateTemplateQuestion(tenantId, templateId, questionId, data),
    onSuccess: (response, variables) => {
      // Invalidate specific template question
      queryClient.invalidateQueries({
        queryKey: questionQueryKeys.templateQuestion(tenantId, templateId, variables.questionId),
      });

      // Invalidate questions list for this template
      queryClient.invalidateQueries({
        queryKey: questionQueryKeys.questionsByTemplate(tenantId, templateId),
      });

      notifications.success('Template question updated successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to update template question';
      notifications.error(message);
    },
  });
};

// Delete template question mutation
export const useDeleteTemplateQuestion = (tenantId: string, templateId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: (questionId: string) =>
      questionApi.deleteTemplateQuestion(tenantId, templateId, questionId),
    onSuccess: (_, questionId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: questionQueryKeys.templateQuestion(tenantId, templateId, questionId),
      });

      // Invalidate questions list for this template
      queryClient.invalidateQueries({
        queryKey: questionQueryKeys.questionsByTemplate(tenantId, templateId),
      });

      notifications.success('Template question deleted successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to delete template question';
      notifications.error(message);
    },
  });
};

// Reorder template questions mutation
export const useReorderTemplateQuestions = (tenantId: string, templateId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: (data: ReorderQuestionsDTO) =>
      questionApi.reorderTemplateQuestions(tenantId, templateId, data),
    onSuccess: (response) => {
      // Silently sync cache with server response instead of invalidating.
      if (response?.data?.updatedQuestions) {
        const updatedMap = new Map(
          response.data.updatedQuestions.map((q: QuestionDTO) => [q.id, q])
        );
        queryClient.setQueryData(
          questionQueryKeys.questionsByTemplate(tenantId, templateId),
          (oldData: unknown) => {
            const typed = oldData as { data?: { questions?: QuestionDTO[] } } | undefined;
            if (!typed?.data?.questions) return oldData;

            return {
              ...typed,
              data: {
                ...typed.data,
                questions: typed.data.questions.map((q) => updatedMap.get(q.id) ?? q),
              },
            };
          }
        );
      }

      notifications.success('Template questions reordered successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to reorder template questions';
      notifications.error(message);
    },
  });
};

// Comprehensive hook for Template Question Builder with all mutations
export const useTemplateQuestions = ({
  tenantId,
  templateId,
}: {
  tenantId: string;
  templateId: string;
}) => {
  const query = useQuestionsByTemplate(tenantId, templateId);
  const createQuestion = useCreateTemplateQuestion(tenantId, templateId);
  const updateQuestion = useUpdateTemplateQuestion(tenantId, templateId);
  const deleteQuestion = useDeleteTemplateQuestion(tenantId, templateId);
  const reorderQuestions = useReorderTemplateQuestions(tenantId, templateId);

  const isUpdating =
    createQuestion.isPending ||
    updateQuestion.isPending ||
    deleteQuestion.isPending ||
    reorderQuestions.isPending;

  return {
    data: query.data?.data?.questions || [],
    isLoading: query.isLoading,
    error: query.error,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    reorderQuestions,
    isUpdating,
  };
};

// Comprehensive hook for Question Builder with all mutations (LEGACY - for survey questions)
export const useQuestions = ({
  surveyId,
  templateId: _templateId,
}: {
  surveyId: string;
  templateId?: string;
}) => {
  const query = useQuestionsBySurvey(surveyId);
  const createQuestion = useCreateQuestion(surveyId);
  const updateQuestion = useUpdateQuestion(surveyId);
  const deleteQuestion = useDeleteQuestion(surveyId);
  const reorderQuestions = useReorderQuestions(surveyId);

  const isUpdating =
    createQuestion.isPending ||
    updateQuestion.isPending ||
    deleteQuestion.isPending ||
    reorderQuestions.isPending;

  return {
    data: query.data?.data?.questions || [],
    isLoading: query.isLoading,
    error: query.error,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    reorderQuestions,
    isUpdating,
  };
};
